import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import React, { useCallback, useEffect, useState } from 'react'

import { fetchRevenueGoalSummary, upsertRevenueGoal } from 'api'
import { showToast } from 'app/helpers'

import GuideButton from './GuideButton'
import { inr, monthShort, planningMonths } from './monthHelpers'
import { useFillHeight } from './useFillHeight'

// Finance's half-year grid (user decision 2026-07-16: 6 months everywhere):
// the next SIX months side by side, one row per centre, Target per month.
// An empty target input shows the suggested run-rate (previous month's
// committed) as its placeholder — a hint, never auto-filled. Each month
// header has a copy button that fills EMPTY targets from the month before.
// Saves are per cell on blur — optimistic, no refetch.
// The manual Override column was removed 2026-07-16: committed adjustments
// now come from the "Use Finance as committed" sync on the All Centres
// board (the committed_override field stays — the sync writes it).
// Past months freeze server-side (403) once they end; this grid only ever
// shows forward months so the lock is mostly invisible here.
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: { marginBottom: theme.spacing(2) },
  tableWrap: { maxHeight: '68vh' },
  empty: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  muted: { color: theme.palette.text.secondary },
  cellInput: { width: 118 },
  monthHead: { borderLeft: `2px solid ${theme.palette.divider}` },
  monthCellFirst: { borderLeft: `2px solid ${theme.palette.divider}` },
  // Two sticky header rows: MUI pins every head cell at top:0, which would
  // overlap them. Push the second (label) row down by the first row's height.
  secondHeaderRow: { '& th': { top: 37 } },
  committedHint: { fontSize: 11, color: theme.palette.text.secondary }
}))

const numberOrNull = (raw) => {
  const trimmed = String(raw ?? '').trim()
  if (trimmed === '') return null
  const value = Number(trimmed)
  return Number.isNaN(value) ? undefined : value
}

// 'YYYY-MM' -> the month before it.
const prevMonthKey = (month) => {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 2, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const SetTargets = () => {
  const classes = useStyles()
  const months = planningMonths(6)
  const [byMonth, setByMonth] = useState({}) // month -> {centres:[], drafts:{}}
  const [centres, setCentres] = useState([])
  const [loading, setLoading] = useState(true)
  const [editable, setEditable] = useState(true)
  const [copying, setCopying] = useState(null) // month being copied into
  const [tableRef, tableMaxHeight] = useFillHeight()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const results = await Promise.all(
        months.map((m) => fetchRevenueGoalSummary(m))
      )
      const next = {}
      results.forEach((data, i) => {
        const drafts = {}
        ;(data?.centres || []).forEach((row) => {
          drafts[row.centre_uuid] = {
            target_amount: row.target ?? '',
            suggested: row.suggested_target,
            committed: row.committed?.total ?? 0
          }
        })
        next[months[i]] = drafts
      })
      setByMonth(next)
      setCentres(results[0]?.centres || [])
      setEditable(results[0]?.editable !== false)
    } catch (error) {
      showToast('Could not load targets', 'error')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const setDraft = (month, centreUuid, field, value) =>
    setByMonth((prev) => ({
      ...prev,
      [month]: {
        ...prev[month],
        [centreUuid]: { ...prev[month]?.[centreUuid], [field]: value }
      }
    }))

  const save = async (month, row, field, rawOverride) => {
    const draft = byMonth[month]?.[row.centre_uuid] || {}
    const raw = rawOverride !== undefined ? rawOverride : draft[field]
    const value = numberOrNull(raw)
    if (value === undefined) {
      showToast('Enter a number (or leave blank)', 'error')
      return
    }
    if (field === 'target_amount' && value === null) return
    try {
      await upsertRevenueGoal({
        centre_uuid: row.centre_uuid,
        month,
        [field]: value
      })
    } catch (error) {
      const detail = error?.response?.data?.detail
      showToast(detail || `Could not save for ${row.centre_name}`, 'error')
    }
  }

  // Fill this month's EMPTY target boxes from the previous month's targets —
  // non-destructive: a target already typed is never overwritten.
  const copyFromPrev = async (month) => {
    setCopying(month)
    try {
      const prev = prevMonthKey(month)
      const data = await fetchRevenueGoalSummary(prev)
      const prevTargets = {}
      ;(data?.centres || []).forEach((r) => {
        if (r.target !== null && r.target !== undefined) {
          prevTargets[r.centre_uuid] = r.target
        }
      })
      const rows = centres.filter((r) => {
        const current = byMonth[month]?.[r.centre_uuid]?.target_amount
        return (
          (current === '' || current === null || current === undefined) &&
          prevTargets[r.centre_uuid] !== undefined
        )
      })
      if (rows.length === 0) {
        showToast(
          `Nothing to copy from ${monthShort(prev)} — targets already set (or none set there)`
        )
        return
      }
      await Promise.all(
        rows.map((r) =>
          upsertRevenueGoal({
            centre_uuid: r.centre_uuid,
            month,
            target_amount: prevTargets[r.centre_uuid]
          })
        )
      )
      rows.forEach((r) =>
        setDraft(month, r.centre_uuid, 'target_amount', prevTargets[r.centre_uuid])
      )
      showToast(`Copied ${rows.length} targets from ${monthShort(prev)}`)
    } catch (error) {
      showToast('Could not copy targets', 'error')
    } finally {
      setCopying(null)
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="h5">Revenue Goals — Set Targets</Typography>
          <GuideButton title="How Set Targets works">
            <Typography variant="body2" gutterBottom>
              Plan the next six months in one sitting — one row per centre,
              one target per month. Everything saves as you click away from a
              box.
            </Typography>
            <Typography variant="body2" gutterBottom style={{ fontWeight: 700, marginTop: 10 }}>
              Target ₹
            </Typography>
            <Typography variant="body2" gutterBottom>
              The centre&apos;s revenue goal, GST-inclusive (tuition + AIM +
              Active Care). The grey &quot;sugg&quot; number in an empty box is
              last month&apos;s committed — a starting point, not a decision.
            </Typography>
            <Typography variant="body2" gutterBottom style={{ fontWeight: 700, marginTop: 10 }}>
              Copy from last month
            </Typography>
            <Typography variant="body2" gutterBottom>
              The copy icon beside a month fills that month&apos;s empty boxes
              with the previous month&apos;s targets. Targets already typed are
              never overwritten.
            </Typography>
            <Typography variant="body2" gutterBottom style={{ fontWeight: 700, marginTop: 10 }}>
              Where committed comes from
            </Typography>
            <Typography variant="body2" gutterBottom>
              Committed is computed by Alfred (advance fees + wallets) and
              corrected by the &quot;Use Finance as committed&quot; button on
              the All Centres board, which adopts PayFlow&apos;s numbers as the
              actuals. There is no manual override entry here any more.
            </Typography>
            <Typography variant="body2">
              Once a month ends its targets freeze, and a week later the whole
              month locks. Centre users see this grid read-only.
            </Typography>
          </GuideButton>
        </Box>
        {!editable && (
          <Typography variant="body2" className={classes.muted}>
            You have view-only access — targets are set by HQ/finance.
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box className={classes.empty}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          ref={tableRef}
          style={{ maxHeight: tableMaxHeight }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                {months.map((m) => (
                  <TableCell
                    key={m}
                    align="center"
                    className={classes.monthHead}
                  >
                    <b>{monthShort(m)}</b>
                    {editable && (
                      <Tooltip
                        title={`Copy ${monthShort(prevMonthKey(m))} targets into ${monthShort(m)}'s empty boxes`}
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={copying === m}
                            onClick={() => copyFromPrev(m)}
                            style={{ marginLeft: 4 }}
                          >
                            <FileCopyOutlinedIcon style={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className={classes.secondHeaderRow}>
                <TableCell>Centre</TableCell>
                {months.map((m) => (
                  <TableCell key={m} className={classes.monthCellFirst}>
                    <Tooltip title="The revenue goal for the centre that month — what the team is asked to reach">
                      <span>Target ₹</span>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {centres.map((row) => (
                <TableRow key={row.centre_uuid} hover>
                  <TableCell>
                    {row.centre_name}
                    <div className={classes.committedHint}>
                      committed {inr(byMonth[months[0]]?.[row.centre_uuid]?.committed || 0)}
                    </div>
                  </TableCell>
                  {months.map((m) => {
                    const draft = byMonth[m]?.[row.centre_uuid] || {}
                    return (
                      <TableCell key={m} className={classes.monthCellFirst}>
                        <TextField
                          size="small"
                          variant="outlined"
                          className={classes.cellInput}
                          value={draft.target_amount ?? ''}
                          disabled={!editable}
                          onChange={(e) =>
                            setDraft(m, row.centre_uuid, 'target_amount', e.target.value)
                          }
                          onBlur={() => save(m, row, 'target_amount')}
                          placeholder={
                            draft.suggested ? `sugg ${Math.round(draft.suggested)}` : ''
                          }
                        />
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
              {centres.length > 0 && (
                <TableRow>
                  <TableCell style={{ fontWeight: 700 }}>All centres</TableCell>
                  {months.map((m) => {
                    const drafts = byMonth[m] || {}
                    const sum = (field) =>
                      Object.values(drafts).reduce((s, d) => {
                        const v = Number(String(d?.[field] ?? '').replace(/,/g, ''))
                        return s + (Number.isNaN(v) ? 0 : v)
                      }, 0)
                    return (
                      <TableCell
                        key={m}
                        className={classes.monthCellFirst}
                        style={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                      >
                        {inr(sum('target_amount'))}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              {centres.length === 0 && (
                <TableRow>
                  <TableCell colSpan={1 + months.length} className={classes.empty}>
                    No centres found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default SetTargets
